import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const navLinks = [
  { label: 'Início', href: '#home' },
  { label: 'Sobre', href: '#about' },
  { label: 'Serviços', href: '#services' },
  { label: 'Produtos', href: '#products' },
  { label: 'Contacto', href: '#contact' },
];

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNav = (href: string) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <motion.nav
      style={styles.nav(scrolled)}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div style={styles.inner}>
        {/* Logo */}
        <button style={styles.logo} onClick={() => handleNav('#home')}>
          <span style={styles.logoMark}>2GF</span>
          <span style={styles.logoText}>Innovation Systems</span>
        </button>

        {/* Desktop Links */}
        <ul style={styles.links}>
          {navLinks.map(link => (
            <li key={link.href}>
              <button style={styles.link} onClick={() => handleNav(link.href)}>
                {link.label}
              </button>
            </li>
          ))}
          <li>
            <button style={styles.ctaBtn} onClick={() => handleNav('#contact')}>
              Fale Connosco
            </button>
          </li>
        </ul>

        {/* Mobile toggle */}
        <button style={styles.menuBtn} onClick={() => setOpen(!open)} aria-label="menu">
          {open ? <X size={24} color="white" /> : <Menu size={24} color="white" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            style={styles.mobileMenu}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            {navLinks.map(link => (
              <button key={link.href} style={styles.mobileLink} onClick={() => handleNav(link.href)}>
                {link.label}
              </button>
            ))}
            <button style={styles.mobileCta} onClick={() => handleNav('#contact')}>
              Fale Connosco
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

const styles = {
  nav: (scrolled: boolean): React.CSSProperties => ({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    background: scrolled ? 'rgba(2,15,30,0.97)' : 'transparent',
    backdropFilter: scrolled ? 'blur(12px)' : 'none',
    borderBottom: scrolled ? '1px solid rgba(255,255,255,0.06)' : 'none',
    transition: 'all 0.4s ease',
  }),
  inner: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
    height: '85px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  } as React.CSSProperties,
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  } as React.CSSProperties,
  logoMark: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '1.5rem',
    fontWeight: 900,
    color: '#0057FF',
    letterSpacing: '-0.02em',
  } as React.CSSProperties,
  logoText: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'rgba(255,255,255,0.85)',
    letterSpacing: '0.02em',
  } as React.CSSProperties,
  links: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    listStyle: 'none',
  } as React.CSSProperties,
  link: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'rgba(255,255,255,0.8)',
    padding: '0.5rem 1rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    transition: 'color 0.2s',
    letterSpacing: '0.02em',
  } as React.CSSProperties,
  ctaBtn: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.8rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase' as const,
    color: 'white',
    background: '#0057FF',
    padding: '0.65rem 1.5rem',
    border: 'none',
    cursor: 'pointer',
    transition: 'background 0.2s',
    marginLeft: '1rem',
  } as React.CSSProperties,
  menuBtn: {
    display: 'none',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.5rem',
  } as React.CSSProperties,
  mobileMenu: {
    background: 'rgba(2,15,30,0.98)',
    padding: '1rem 2rem 2rem',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
    overflow: 'hidden',
  } as React.CSSProperties,
  mobileLink: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '1rem',
    fontWeight: 500,
    color: 'rgba(255,255,255,0.85)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.75rem 0',
    textAlign: 'left' as const,
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  mobileCta: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.85rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase' as const,
    color: 'white',
    background: '#0057FF',
    padding: '1rem',
    marginTop: '0.5rem',
    border: 'none',
    cursor: 'pointer',
  },
};

export default Navbar;
